<script>
import { h } from "vue";
import SecondaryPageLayout from "@/Layouts/SecondaryPageLayout.vue";

export default {
    layout: (_, page) =>
        h(
            SecondaryPageLayout,
            {
                title: "The Gift Hero Browser Extension",
                titleClass: "max-w-[475px]",
            },
            () => page,
        ),
};
</script>

<script setup>
import { computed } from "vue";
import { Head, Link, usePage } from "@inertiajs/vue3";
import BrandButton from "@/Components/Brand/BrandButton.vue";

const {
    browser,
    is_mobile,
    is_tablet,
    is_supported,
    supported_extensions,
    store_urls,
} = usePage().props.browser_info;

const isHandheld = is_mobile || is_tablet;

// Button glyphs are the browsers' own marks, so they render as images rather
// than a currentColor mask.
const browserGlyphs = {
    Chrome: "images/brand/browser-chrome.png",
    Edge: "images/brand/browser-edge.png",
};

// One install CTA per extension we ship, the visitor's own browser first.
const installTargets = computed(() =>
    Object.keys(supported_extensions)
        .sort((a, b) => (a === browser ? -1 : b === browser ? 1 : 0))
        .map((key) => ({
            key,
            href: store_urls[key],
            label: `Install for ${key}`,
            title: `Install the Gift Hero extension for ${supported_extensions[key]}`,
            glyph: browserGlyphs[key],
        })),
);
</script>

<template>
    <Head>
        <title>Browser Extensions</title>
        <meta
            name="description"
            content="The Gift Hero browser extension makes it easy to save to your wish lists."
        />
        <meta
            property="og:title"
            content="Download the Gift Hero browser extension"
        />
        <meta
            property="og:description"
            content="The Gift Hero browser extension makes it easy to save to your wish lists."
        />
        <meta property="og:url" :content="route('browser-extensions')" />
        <link rel="canonical" :href="route('browser-extensions')" />
    </Head>

    <!--
        Copy on the left, the browser mockup (flat Figma export, 397×385 @1x)
        flush with the column's right edge from lg; below lg the mockup
        follows the copy in flow.
    -->
    <div class="lg:flex lg:items-start lg:justify-between lg:gap-10">
        <div class="max-w-[507px]">
            <h2
                class="font-brand-display text-[36px] font-black italic uppercase leading-[1.15] tracking-[-0.02em] text-[#102a3d] md:text-[42px]"
            >
                The fastest way to add gifts to your wish lists.
            </h2>
            <p
                class="mb-0 mt-6 text-[18px] font-semibold leading-tight tracking-[-0.02em] text-[#102a3d] md:text-[20px]"
            >
                Add anything to your Gift Hero wish list with just a click.
            </p>
            <p
                class="mb-0 mt-5 text-[18px] leading-[1.5] tracking-[-0.02em] text-[#102a3d] md:text-[20px]"
            >
                Saving gifts to your wish list has never been easier! Add an
                item from any online retailer to your Gift Hero wish lists just
                by clicking the extension on any site you’re browsing.
            </p>

            <p
                v-if="isHandheld"
                class="mb-0 mt-5 text-[16px] leading-[1.5] tracking-[-0.02em] text-[#102a3d]"
            >
                Browsing on a phone or tablet? The extension is for desktop
                browsers, so
                <Link
                    :href="route('mobile')"
                    class="font-semibold text-brand-red underline underline-offset-2 hover:text-brand-navy"
                    >get the Gift Hero mobile app</Link
                >
                instead. On your computer, install it for:
            </p>
            <p
                v-else-if="!is_supported"
                class="mb-0 mt-5 text-[16px] leading-[1.5] tracking-[-0.02em] text-[#102a3d]"
            >
                Sorry, we don’t have an extension for {{ browser }} yet. It’s
                available for:
            </p>

            <ul class="mb-0 mt-9 flex list-none flex-wrap gap-x-8 gap-y-4 p-0">
                <li v-for="target in installTargets" :key="target.key">
                    <BrandButton
                        :href="target.href"
                        :title="target.title"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="h-[52px] px-[27px] text-[15px]"
                    >
                        <template #icon>
                            <img
                                :src="asset(target.glyph)"
                                alt=""
                                aria-hidden="true"
                                width="22"
                                height="22"
                                class="size-[22px]"
                            />
                        </template>
                        {{ target.label }}
                    </BrandButton>
                </li>
            </ul>
        </div>

        <img
            :src="asset('images/brand/browser-extension-mockup.webp')"
            alt="The Gift Hero extension open in a browser, saving a gift to the Decorations wish list"
            width="397"
            height="385"
            class="mt-12 h-auto w-full max-w-[397px] shrink-0 rounded-[5px] lg:mt-0"
        />
    </div>
</template>
